/**
 * features/parent/PhotoNeededCard.tsx
 * ------------------------------------------------------------------
 * "Add <child>'s photo" — put where a parent lands, for every linked child
 * the school has no photo of (Oct 2026).
 *
 * Parents were emailed every week to add a photo and could not find where:
 * the upload sits on the child's Profile tab, and the portal opens on the
 * dashboard and then the Academic tab. Several wrote back asking how. This
 * card is the answer on screen: one button per child, straight to the
 * upload (ChildPhotoCard on the Profile tab).
 *
 * It shows whether or not the child is on a fees hold. The hold pauses
 * marks, reports and attendance; a photo and contact details are not
 * academic, and the school wants them from every family (the school,
 * 26 Oct 2026 — 327 of the 330 held pupils had no photo).
 * ------------------------------------------------------------------ */
import { Camera } from 'lucide-react';
import { SoftCard } from '@/features/finance/ui';
import type { Student } from '@/types';

/* THE FULL NAME, AS THE PORTAL SHOWS IT EVERYWHERE. A first name cannot be
   picked out safely: full names are kept in both orders ("Matshane Atang",
   "Teto Kgalemang"), and the admissions first name is sometimes the surname
   (Joubert Daphney G.'s says "Joubert"). */
export default function PhotoNeededCard({ students, onAdd }: { students: Student[]; onAdd: (studentId: string) => void }) {
  const need = students.filter((s) => !s.photo?.path);
  if (!need.length) return null;
  return (
    <SoftCard>
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 shrink-0 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 flex items-center justify-center">
          <Camera size={18} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 dark:text-white">
            {need.length === 1 ? `Please add a photo of ${need[0].fullName}` : 'Please add photos of your children'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            The school needs a recent, clear photo of each child — head and shoulders, facing the camera.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {need.map((s) => (
              <button
                key={s.id}
                onClick={() => onAdd(s.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[var(--accent-strong)] text-white hover:opacity-90"
              >
                <Camera size={14} /> {need.length === 1 ? 'Add photo' : `Add photo: ${s.fullName}`}
              </button>
            ))}
          </div>
        </div>
      </div>
    </SoftCard>
  );
}
